import { formatDateShortDateAndMonth } from '~/services/TimeService';

export type CsAgent = {
  PersonID: number;
  Name: string;
  Email: string;
  SupervisorID: number;
  subordinates?: Array<CsAgent>;
};

export type AgentTableRow = {
  email: string;
  agentId: string;
  dayCounts: Array<{ day: number; count: number; date: string }>;
  total: number;
};

/**
 * Extracts all subordinate emails for a given team leader
 */
export function getSubordinateEmails(teamLeaderEmail: string, agents: Array<CsAgent>): Set<string> {
  const emails = new Set<string>();

  function findAndCollect(agentList: Array<CsAgent>) {
    for (const agent of agentList) {
      if (agent.Email.toLowerCase() === teamLeaderEmail.toLowerCase()) {
        // Found the team leader, collect all subordinates
        if (agent.subordinates) {
          for (const subordinate of agent.subordinates) {
            emails.add(subordinate.Email.toLowerCase());
          }
        }
        return;
      }
      // Recursively search in subordinates
      if (agent.subordinates) {
        findAndCollect(agent.subordinates);
      }
    }
  }

  findAndCollect(agents);
  return emails;
}

/**
 * Gets filtered emails for chart when team leader is selected.
 * Includes the team leader's email as well as all subordinates.
 */
export function getFilteredEmailsForChart(selectedTeamLeader: string, agents: Array<CsAgent>): Set<string> | undefined {
  if (!selectedTeamLeader) {
    return undefined;
  }
  const subordinateEmails = getSubordinateEmails(selectedTeamLeader, agents);
  // Include the team leader's email as well
  subordinateEmails.add(selectedTeamLeader.toLowerCase());
  return subordinateEmails;
}

/**
 * Generates day column headers (21, 20, 19, ..., 1)
 */
export function generateDayHeaders(): Array<number> {
  return Array.from({ length: 21 }, (_, i) => 21 - i);
}

/**
 * Calculates maximum count across all agents and days in the table data
 */
export function calculateMaxCount(tableData: Array<AgentTableRow>): number {
  if (!tableData || tableData.length === 0) {
    return 1; // Avoid division by zero
  }
  let max = 0;
  for (const row of tableData) {
    for (const dayCount of row.dayCounts) {
      if (dayCount.count > max) {
        max = dayCount.count;
      }
    }
  }
  return max || 1; // Return at least 1 to avoid division by zero
}

/**
 * Gets color based on count (red for 0, green for max).
 * Returns an RGB color string interpolated between red and green.
 */
export function getCountColor(count: number, maxCount: number): string {
  if (maxCount === 0) {
    return 'rgb(255, 200, 200)'; // Light red for zero
  }
  // Normalize count to 0-1 range
  const ratio = count / maxCount;
  // Interpolate between red (255, 200, 200) and green (200, 255, 200)
  const red = Math.round(255 - ratio * 55); // 255 -> 200
  const green = Math.round(200 + ratio * 55); // 200 -> 255
  const blue = 200; // Keep blue constant for a cleaner gradient
  return `rgb(${red}, ${green}, ${blue})`;
}

/**
 * Formats YYYYMMDD date string to "D MMM" format
 */
export function formatDateString(dateStr: string): string {
  if (dateStr && dateStr.length === 8) {
    const year = parseInt(dateStr.substring(0, 4), 10);
    const month = parseInt(dateStr.substring(4, 6), 10) - 1; // Month is 0-indexed
    const day = parseInt(dateStr.substring(6, 8), 10);
    const date = new Date(year, month, day);
    return formatDateShortDateAndMonth(date);
  }
  return dateStr;
}
